import {SocialMediaPublishPhase} from '@Libraries/social-media-publish-phase.library';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {SocialPost} from '@Components/social-media/post.vo';
import {PostState} from '@Components/social-media/post.vo';
import {SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {WizardFlowAdditionalData} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import type {GalleryPageURI} from '@Components/design-gallery/design-gallery.types';
import {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {GallerySizeURI, GalleryThemeURI} from '@Components/design-gallery/design-gallery.types';
import type {SocialPlatform} from '@Components/social-media/social-media.types';
import {isLocal} from '@Libraries/environment-library';
import type {PublishedSlotCounts} from '@Libraries/social-media-wizard-accounts-library';
import {assertNever} from '@Utils/assert-never.util';

const BLOCKED_SCHEDULE_STATES = new Set<PostState>([PostState.SCHEDULED, PostState.PUBLISHING, PostState.DRAFT, PostState.ERROR, PostState.SCHEDULE_ERROR]);

const CONTENT_GROUP_STEPS: ReadonlySet<SocialMediaWizardStep> = new Set([SocialMediaWizardStep.SELECT_DESIGN, SocialMediaWizardStep.EDIT_TEMPLATE]);
const ACCOUNTS_GROUP_STEPS: ReadonlySet<SocialMediaWizardStep> = new Set([SocialMediaWizardStep.ACCOUNT_CONNECTION, SocialMediaWizardStep.SELECT_ACCOUNTS]);

export const isAccountRelatedStep = (step: SocialMediaWizardStep): boolean => {
  return step === SocialMediaWizardStep.ACCOUNT_CONNECTION || step === SocialMediaWizardStep.SELECT_ACCOUNTS;
};

export const hasMobilePreviewAction = (step: SocialMediaWizardStep): boolean => {
  return step === SocialMediaWizardStep.ADD_DETAILS || isAccountRelatedStep(step);
};

export const shouldShowScheduleDetails = (post: SocialPost | undefined, step: SocialMediaWizardStep): boolean => {
  if (!post?.state) {
    return false;
  }

  if (BLOCKED_SCHEDULE_STATES.has(post.state)) {
    return false;
  }

  return !!(post.publishOn && post.timeZoneOffset !== null && step === SocialMediaWizardStep.ADD_DETAILS);
};

interface NextButtonConfig {
  text: string;
  icon: string;
}

interface GetNextButtonConfigOpts {
  step: SocialMediaWizardStep;
  post: SocialPost | undefined;
  isSelectTemplateFlow: boolean;
}

export const getNextButtonConfig = (opts: GetNextButtonConfigOpts): NextButtonConfig => {
  const {step, post, isSelectTemplateFlow} = opts;

  if (isSelectTemplateFlow && isAccountRelatedStep(step)) {
    return {text: window.i18next.t('pmwjs_next_edit_template'), icon: 'icon-arrow-right'};
  }

  if (step === SocialMediaWizardStep.ADD_DETAILS) {
    if (shouldShowScheduleDetails(post, step)) {
      return {text: window.i18next.t('pmwjs_schedule'), icon: 'icon-publish-arrow'};
    }

    return {text: window.i18next.t('pmwjs_post'), icon: 'icon-publish-arrow'};
  }

  return {text: window.i18next.t('pmwjs_next'), icon: 'icon-arrow-right'};
};

const getLoadedTextByPublishingAction = (publishingAction: SocialPostPublishingAction): string => {
  switch (publishingAction) {
    case SocialPostPublishingAction.PUBLISH:
      return window.i18next.t('pmwjs_social_posted_successfully_heading');
    case SocialPostPublishingAction.SCHEDULE:
      return window.i18next.t('pmwjs_social_scheduled_successfully_heading');
    case SocialPostPublishingAction.SAVE:
      return window.i18next.t('pmwjs_saved_as_draft');
    default:
      return assertNever(publishingAction);
  }
};

const getLoadingTextByPublishingAction = (publishingAction: SocialPostPublishingAction): string => {
  switch (publishingAction) {
    case SocialPostPublishingAction.PUBLISH:
      return window.i18next.t('pmwjs_publishing');
    case SocialPostPublishingAction.SCHEDULE:
      return window.i18next.t('pmwjs_scheduling');
    case SocialPostPublishingAction.SAVE:
      return window.i18next.t('pmwjs_saving');
    default:
      return window.i18next.t('pmwjs_publishing');
  }
};

const getAllSlotsFailedText = (publishedSlotCounts: PublishedSlotCounts): string => {
  return window.i18next.t('pmwjs_social_your_post_could_not_be_published', {count: publishedSlotCounts.totalCount});
};

/**
 * Only PUBLISH can settle with a mix of published and failed slots, so only it gets the "Almost done" count;
 * SCHEDULE and SAVE set their statuses wholesale, where settled-with-failures means the whole action failed.
 */
const getSettledWithFailuresTextByPublishingAction = (publishingAction: SocialPostPublishingAction, publishedSlotCounts: PublishedSlotCounts): string => {
  switch (publishingAction) {
    case SocialPostPublishingAction.PUBLISH:
      return publishedSlotCounts.publishedCount > 0
        ? window.i18next.t('pmwjs_social_almost_done_x_posts_failed', {count: publishedSlotCounts.totalCount - publishedSlotCounts.publishedCount})
        : getAllSlotsFailedText(publishedSlotCounts);
    case SocialPostPublishingAction.SCHEDULE:
    case SocialPostPublishingAction.SAVE:
      return getAllSlotsFailedText(publishedSlotCounts);
    default:
      return assertNever(publishingAction);
  }
};

export interface PublishingPanelHeadingState {
  publishingAction: SocialPostPublishingAction | undefined;
  haveAllPostsPublished: boolean;
  haveAllPostsSettled: boolean;
  publishedSlotCounts: PublishedSlotCounts;
}

/** Precedence: full success, then settled-with-failures, then the loading text for anything in flight. */
export const getPublishingPanelHeading = ({publishingAction, haveAllPostsPublished, haveAllPostsSettled, publishedSlotCounts}: PublishingPanelHeadingState): string => {
  if (!publishingAction) {
    return window.i18next.t('pmwjs_publishing');
  }

  if (haveAllPostsPublished) {
    return getLoadedTextByPublishingAction(publishingAction);
  }

  if (haveAllPostsSettled) {
    return getSettledWithFailuresTextByPublishingAction(publishingAction, publishedSlotCounts);
  }

  return getLoadingTextByPublishingAction(publishingAction);
};

const getStepForAccounts = (accounts: SocialAccount[], platformToEmphasize?: SocialPlatform): SocialMediaWizardStep => {
  if (platformToEmphasize && !doesAnyAccountHavePlatform(accounts, platformToEmphasize)) {
    return SocialMediaWizardStep.ACCOUNT_CONNECTION;
  }

  return SocialMediaWizardStep.SELECT_ACCOUNTS;
};

const doesAnyAccountHavePlatform = (accounts: SocialAccount[], platform: SocialPlatform): boolean => {
  return accounts.some(function (account) {
    return account.type === platform;
  });
};

const isSpecificTemplateNotSelected = (wizardFlowData: WizardFlowAdditionalData | null): boolean => {
  return !wizardFlowData?.template;
};

export const getNextWizardStep = (
  post?: SocialPost,
  accounts: SocialAccount[] = [],
  step: SocialMediaWizardStep | null = null,
  wizardFlowData: WizardFlowAdditionalData | null = null
): SocialMediaWizardStep | null => {
  if (!step && isSpecificTemplateNotSelected(wizardFlowData)) {
    if (!post) {
      return SocialMediaWizardStep.SELECT_DESIGN;
    }

    return getStepForAccounts(accounts, wizardFlowData?.emphasizedPlatform);
  }

  if (!step && wizardFlowData?.template) {
    return getStepForAccounts(accounts, wizardFlowData.emphasizedPlatform);
  }

  switch (step) {
    case SocialMediaWizardStep.SELECT_DESIGN:
      return getStepForAccounts(accounts, wizardFlowData?.emphasizedPlatform);
    case SocialMediaWizardStep.ACCOUNT_CONNECTION:
      return SocialMediaWizardStep.SELECT_ACCOUNTS;
    case SocialMediaWizardStep.SELECT_ACCOUNTS:
      if (wizardFlowData && (wizardFlowData.isGalleryFlow || wizardFlowData.template)) {
        return SocialMediaWizardStep.EDIT_TEMPLATE;
      }

      return SocialMediaWizardStep.ADD_DETAILS;

    case SocialMediaWizardStep.EDIT_TEMPLATE:
      return SocialMediaWizardStep.ADD_DETAILS;
    case SocialMediaWizardStep.ADD_DETAILS:
      return SocialMediaWizardStep.PUBLISH;
    default:
      return null;
  }
};

export const getStartingURIForGallery = (): GalleryPageURI => {
  // Templates exist on local for this category
  if (isLocal()) {
    return GalleryThemeURI.RETAIL;
  }

  return GallerySizeURI.SOCIAL_MEDIA;
};

export const isInvalidStepCombination = (prevStep: SocialMediaWizardStep | null, currentStep: SocialMediaWizardStep, post: SocialPost | null): boolean => {
  return (prevStep === SocialMediaWizardStep.SELECT_DESIGN || currentStep === SocialMediaWizardStep.SELECT_DESIGN) && post !== null;
};

export enum FooterProgressStep {
  DESIGN = 1,
  ACCOUNTS = 2,
  REVIEW = 3,
}

export interface WizardFooterProgressSteps {
  currentStep: FooterProgressStep;
  numSteps: number;
  stepLabels: string[];
}

export const getFooterProgressSteps = (step: SocialMediaWizardStep): WizardFooterProgressSteps => {
  const stepLabels = [window.i18next.t('pmwjs_design'), window.i18next.t('pmwjs_accounts'), window.i18next.t('pmwjs_review')];

  const getCurrentStep = (): FooterProgressStep => {
    if (CONTENT_GROUP_STEPS.has(step)) {
      return FooterProgressStep.DESIGN;
    }

    if (ACCOUNTS_GROUP_STEPS.has(step)) {
      return FooterProgressStep.ACCOUNTS;
    }

    return FooterProgressStep.REVIEW;
  };

  return {currentStep: getCurrentStep(), numSteps: stepLabels.length, stepLabels};
};

export const getInitialDesignSelectorTab = (deepLinkedTab: DesignSelectorTab | null, isGalleryFlow: boolean): DesignSelectorTab => {
  if (deepLinkedTab) {
    return deepLinkedTab;
  }

  if (isGalleryFlow) {
    return DesignSelectorTab.GALLERY_TEMPLATES;
  }

  return DesignSelectorTab.MY_DESIGNS;
};

/** The footer holds the layout it was submitted from, so its actions freeze instead of reflowing mid-publish. */
export const getFooterLayoutStep = (wizardStep: SocialMediaWizardStep, publishPhase: SocialMediaPublishPhase): SocialMediaWizardStep => {
  return publishPhase === SocialMediaPublishPhase.IN_FLIGHT && wizardStep === SocialMediaWizardStep.PUBLISH ? SocialMediaWizardStep.ADD_DETAILS : wizardStep;
};
